import { apiClient } from './client';
import { extractBlobErrorMessage } from './payments';
import { PaginatedResponse } from '../types/pagination';
import { PaymentStatus, PaymentType } from '../types/payment';
import {
  FinanceAuditEvent,
  FinanceDashboard,
  FinanceFilters,
  FinanceInvoiceRow,
  FinanceLedgerRow,
  FinanceLookups,
  FinancePaymentStatus,
  FinanceReceiptRow,
  FinanceReport,
  FinanceStudent,
  FinanceStudentRow,
  LedgerTotal,
} from '../types/finance';

/** Finance workspace (Phase 96) — /api/finance/*. Recording, confirming
 * and rejecting payments and invoice/receipt PDFs use api/payments.ts and
 * api/students.ts as before. */

/** Drops empty values so they never reach the query string. */
const clean = <T extends object>(params: T) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null),
  );

export const getFinanceLookups = async (): Promise<FinanceLookups> => {
  const { data } = await apiClient.get('/finance/lookups');
  return data.data;
};

export const getFinanceOverview = async (filters: FinanceFilters): Promise<FinanceDashboard> => {
  const { data } = await apiClient.get('/finance/dashboard', { params: clean(filters) });
  return data.data;
};

export interface FinanceStudentsParams extends Omit<FinanceFilters, 'from' | 'to'> {
  page: number;
  pageSize: number;
  search?: string;
  paymentStatus?: FinancePaymentStatus;
  pendingOnly?: boolean;
}

export const listFinanceStudents = async (
  params: FinanceStudentsParams,
): Promise<PaginatedResponse<FinanceStudentRow>> => {
  const { data } = await apiClient.get('/finance/students', {
    params: clean({ ...params, pendingOnly: params.pendingOnly ? 'true' : undefined }),
  });
  return data;
};

export const getFinanceStudent = async (id: number): Promise<FinanceStudent> => {
  const { data } = await apiClient.get(`/finance/students/${id}`);
  return data.data;
};

export const getFinanceAuditHistory = async (id: number): Promise<FinanceAuditEvent[]> => {
  const { data } = await apiClient.get(`/finance/students/${id}/audit-history`);
  return data.data;
};

export interface FinanceLedgerParams extends FinanceFilters {
  page: number;
  pageSize: number;
  search?: string;
  status?: PaymentStatus;
  type?: PaymentType;
}

export const listFinanceLedger = async (
  params: FinanceLedgerParams,
): Promise<PaginatedResponse<FinanceLedgerRow> & { totals: LedgerTotal[] }> => {
  const { data } = await apiClient.get('/finance/ledger', { params: clean(params) });
  return data;
};

/** One payment as a ledger row — the review dialog (Phase 97). */
export const getFinancePayment = async (id: number): Promise<FinanceLedgerRow> => {
  const { data } = await apiClient.get(`/finance/payments/${id}`);
  return data.data;
};

export interface FinanceDocumentsParams extends FinanceFilters {
  page: number;
  pageSize: number;
  search?: string;
  invoiceType?: 'USD' | 'LKR';
}

export const listFinanceInvoices = async (
  params: FinanceDocumentsParams,
): Promise<PaginatedResponse<FinanceInvoiceRow>> => {
  const { data } = await apiClient.get('/finance/invoices', { params: clean(params) });
  return data;
};

export const listFinanceReceipts = async (
  params: FinanceDocumentsParams,
): Promise<PaginatedResponse<FinanceReceiptRow>> => {
  const { data } = await apiClient.get('/finance/receipts', { params: clean(params) });
  return data;
};

export type FinanceExportParams = FinanceFilters & {
  report: FinanceReport;
  format: 'xlsx' | 'pdf';
  search?: string;
  paymentStatus?: FinancePaymentStatus;
  status?: PaymentStatus;
  type?: PaymentType;
  invoiceType?: 'USD' | 'LKR';
};

export const exportFinanceReport = async (
  params: FinanceExportParams,
): Promise<{ blob: Blob; filename: string }> => {
  try {
    const response = await apiClient.get('/finance/reports/export', {
      params: clean(params),
      responseType: 'blob',
    });
    const disposition = (response.headers['content-disposition'] as string | undefined) ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return {
      blob: response.data,
      filename: match?.[1] ?? `imc-${params.report}.${params.format}`,
    };
  } catch (err) {
    throw new Error(await extractBlobErrorMessage(err, "Couldn't export the report"));
  }
};
